import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Platform } from 'react-native';
import { User, LoginRequest, RegisterRequest } from '../types/auth';
import { api } from '../services/api';
import { storage } from '../utils/storage';
import { isDefinitiveAuthFailure } from './sessionHydration';
import { safeImport } from '../utils/staleBundle';
import { analytics } from '../services/analytics';
import { trackIdentified } from '../services/acquisition';
import { pushNotifications } from '../services/pushNotifications';
import { getUserRegion } from '../services/region';
import { setSentryUser, clearSentryUser } from '../utils/sentry';
import { shouldReportApiError } from '../services/errorMessages';
import {
  beginAuthAttempt,
  finishAuthFailure,
  finishAuthSuccess,
  stampCorrelation,
} from '../services/authDiagnostics';
import { setTelemetryUser } from '../services/liveTelemetry';
import { setUploadTelemetryUser } from '../services/uploadTelemetry';
import { setNetTelemetryUser } from '../services/netTelemetry';
import { setMediaDebugUser } from '../services/mediaDebug';
import { syncDeviceTimezone } from '../services/deviceTimezone';
import { setRewardReactionsSnapshot, subscribeRewardReactions } from '../utils/rewardReactionsBus';
import { notifyRewardUnlocked } from '../utils/rewardReactions';
import { clearChatDraftsOf } from '../services/chat/drafts';
import { getJwtExpiryMs, JWT_REFRESH_LEAD_MS } from '../utils/jwtExpiry';

// AMB-200 — sliding JWT silent refresh. Decoding `exp` lives in
// utils/jwtExpiry (CORE-2092: the API client checks it before requests too).

// Decode the read-only impersonation marker an admin "login as" token carries
// (imp_ro/imp_by + the impersonated user's email). Best-effort, display-only —
// the read-only contract is enforced server-side, not here.
function decodeImpersonation(token: string): { readOnly: boolean; email?: string } | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(
      typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('utf8')
    );
    if (json?.imp_ro !== 'true') return null;
    return { readOnly: true, email: typeof json?.email === 'string' ? json.email : undefined };
  } catch {
    return null;
  }
}

// Admin "login as" hand-off: when the main app is opened with
// `?impersonation_token=…`, store it as the auth token and strip it from the URL
// (so it isn't bookmarked/shared). Web-only. Returns the token if ingested.
async function ingestImpersonationTokenFromUrl(): Promise<string | null> {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('impersonation_token');
    if (!token) return null;
    await storage.setToken(token);
    params.delete('impersonation_token');
    const qs = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
    );
    return token;
  } catch {
    return null;
  }
}

// Fallback when the JWT can't be decoded or exp is missing — retry once per
// hour. Safe because the BE accepts a still-valid JWT for refresh.
const JWT_REFRESH_FALLBACK_MS = 60 * 60 * 1000;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** CORE-1200 — `returnTo` только для диагностики: он привязывает попытку к Live, из которого пришли. */
  login: (data: LoginRequest, opts?: { returnTo?: string | null }) => Promise<void>;
  loginWithPasskey: (email?: string) => Promise<void>;
  // CORE-108 — social sign-in. Google hands us an id_token from its button
  // callback; Apple runs the full popup itself. Both finish via the shared
  // post-login work (token store, getMe, refresh timer, prefs).
  /** CORE-513 — возвращают признак первого входа: вызывающий решает прямо в
   *  обработчике, показать приветствие Искателя или пустить в обычный флоу. */
  loginWithGoogleToken: (idToken: string) => Promise<{ isNewUser: boolean }>;
  loginWithApple: () => Promise<{ isNewUser: boolean }>;
  loginWithAppleToken: (payload: { idToken: string; fullName?: string }) => Promise<{ isNewUser: boolean }>;
  /** CORE-1534 — вход по одноразовому коду, с которым приложение открыло страницу оплаты. */
  loginWithHandoffCode: (code: string) => Promise<void>;
  register: (data: RegisterRequest, opts?: { returnTo?: string | null }) => Promise<void>;
  logout: () => Promise<void>;
  /** CORE-1587 — сессию не восстановить (refresh отказал): забыть её без
   *  перехода и аналитики выхода. Экран сам ведёт на вход, сохранив своё. */
  expireSession: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  /** CORE-513 — вошедшему только что новому человеку ещё не показали
   *  приветствие Искателя. Живёт в памяти сессии, не переживает перезагрузку. */
  newUserWelcomePending: boolean;
  clearNewUserWelcome: () => void;
  // Admin "login as": non-null while viewing the app via a read-only
  // impersonation token. Carries the impersonated user's email for the banner.
  impersonation: { readOnly: boolean; email?: string } | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

/** If browser has an existing push subscription, ensure backend knows about it */
async function syncPushSubscription() {
  if (Platform.OS !== 'web' || !pushNotifications.isSupported()) return;
  try {
    const status = await pushNotifications.getStatus();
    if (status.permission === 'granted' && !status.isSubscribed) {
      // Permission granted but no browser subscription — create one and register
      await pushNotifications.subscribe();
    } else if (status.permission === 'granted' && status.isSubscribed) {
      // Browser has subscription — re-register with backend in case it was lost
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const json = subscription.toJSON();
        if (json.endpoint && json.keys?.p256dh && json.keys?.auth) {
          await api.registerPushSubscription({
            endpoint: json.endpoint,
            p256dh: json.keys.p256dh,
            auth: json.keys.auth,
            userAgent: navigator.userAgent,
          });
        }
      }
    }
  } catch {
    // Non-critical — don't break auth flow
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<{ readOnly: boolean; email?: string } | null>(null);
  // AMB-200 — handle for the proactive silent-refresh timer. Held in a ref so
  // we can clear/reschedule across renders without re-creating timers.
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // CORE-1791 — кто выходит: `logout` мемоизирован без зависимостей и `user`
  // из замыкания видел бы первый рендер.
  const userIdRef = useRef<string | null>(null);
  userIdRef.current = user?.id ?? null;

  const isAuthenticated = !!user;

  // AMB-200 — schedule the next silent refresh based on the JWT's `exp`
  // claim. Called after login, after a successful refresh, and on mount when
  // a token is already in storage. Idempotent: clears any previous timer
  // before scheduling. The timer runs as long as the tab is open; browsers
  // may throttle it in hidden tabs, which is fine — when the tab regains
  // focus the timer fires (possibly late) and refreshes before the next
  // network call needs the token.
  const scheduleRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    const expMs = getJwtExpiryMs(token);
    let delay: number;
    if (expMs == null) {
      delay = JWT_REFRESH_FALLBACK_MS;
    } else {
      // `exp - now - lead` is the natural delay; clamp to 30s so a token
      // that's already past the lead window (e.g. tab opened with a JWT
      // ~25min from expiry) refreshes immediately rather than next hour.
      delay = Math.max(30_000, expMs - Date.now() - JWT_REFRESH_LEAD_MS);
    }
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const newToken = await api.refreshJwt();
        if (newToken) {
          analytics.track('jwt_refresh_success');
          scheduleRefresh(newToken);
        } else {
          // Refresh failed (expired or revoked). Don't kick the user out
          // here — let the next 401 from a real API call trigger the
          // normal sign-out flow. Just retry the schedule in a bit.
          analytics.track('jwt_refresh_failed');
          refreshTimerRef.current = setTimeout(
            () => scheduleRefresh(token),
            JWT_REFRESH_FALLBACK_MS
          );
        }
      } catch {
        analytics.track('jwt_refresh_failed');
      }
    }, delay);
  }, []);

  // CORE-1479 — ответ на сохранение профиля несёт свежее состояние наградных
  // реакций. Профиль сохраняют с полудюжины экранов, и не каждый перечитывает
  // /me, а выбор реакций должен открыться сразу — поэтому подхватываем здесь.
  useEffect(() => subscribeRewardReactions(({ state, justUnlocked }) => {
    setUser((prev) => (prev ? { ...prev, rewardReactions: state } : prev));
    if (justUnlocked) notifyRewardUnlocked();
  }), []);

  // CORE-1479 — снимок для выбора реакций (`useRewardReactions` читает его, а не
  // этот контекст: иначе хабы комнаты тянули бы за собой весь AuthContext).
  useEffect(() => {
    setRewardReactionsSnapshot({ signedIn: !!user, state: user ? user.rewardReactions : undefined });
  }, [user]);

  // Check for existing session on mount
  useEffect(() => {
    // AMB-224 — populate the anonymous region cache once per app load,
    // fire-and-forget. For authenticated users we have user.region on
    // /api/auth/me, but anonymous viewers hitting /masters / public
    // profile pages need the cache warm so `isRestrictedRegion(null)`
    // returns the right answer synchronously inside render.
    void getUserRegion();

    const initAuth = async () => {
      try {
        // Admin "login as": pick up a read-only impersonation token from the URL.
        await ingestImpersonationTokenFromUrl();

        const token = await storage.getToken();
        if (!token) return;

        setImpersonation(decodeImpersonation(token));

        // AMB-200 — try /me; if it 401's, the JWT may be expired (overnight
        // tab, app resumed from background after exp). One refresh attempt
        // before falling through to the catch (which clears the token).
        // The api.handleResponse 401 path also clears + redirects, so we
        // gate that with skipAuthRedirect by catching here ourselves.
        let userData: User;
        try {
          userData = await api.getMe();
        } catch (e: any) {
          if (e?.status === 401) {
            const refreshed = await api.refreshJwt();
            if (!refreshed) throw e;
            userData = await api.getMe();
          } else {
            throw e;
          }
        }
        setUser(userData);
        syncPushSubscription();
        scheduleRefresh(await storage.getToken() ?? token);
      } catch (e) {
        // CORE-1266 — only drop the session on a DEFINITIVE auth rejection.
        // A transient failure (network blip, or — the QA-FAIL — the in-flight
        // /me aborted because a path-alias `window.location.replace` tore the
        // page down mid-restore) must NOT delete the token: the cookie is still
        // valid and the next load re-hydrates from it. Clearing it here turned a
        // direct hit on /recordings, /management/series, /templates into a real
        // logout that bounced the authorized master to /login?returnTo=/me.
        if (isDefinitiveAuthFailure(e)) {
          await storage.removeToken();
        }
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [scheduleRefresh]);

  // CORE-308 — keep the Sentry user identity in lock-step with auth state, in
  // one place, so every entry path (restore, login, passkey, Google/Apple,
  // refreshUser, logout) is covered without sprinkling setUser calls. Lets the
  // team answer «кто столкнулся / какая роль» on any captured event.
  useEffect(() => {
    if (user) {
      setSentryUser({ id: user.id, roles: user.roles });
    } else {
      clearSentryUser();
    }
    // CORE-434 — same lock-step for RTC telemetry so beacon-flushed events
    // (which can't carry the auth header) are still attributable to a user.
    setTelemetryUser(user?.id ?? null);
    // CORE-486 — same lock-step for upload telemetry (account attribution for
    // beacon-flushed upload events).
    setUploadTelemetryUser(user?.id ?? null);
    // CORE-604 — same lock-step for network/host-routing telemetry.
    setNetTelemetryUser(user?.id ?? null);
    // CORE-535 — same lock-step for opt-in media-load diagnostics (web only,
    // allowlisted accounts). No-op for everyone not on the allowlist.
    setMediaDebugUser(user?.id ?? null);
  }, [user]);

  // CORE-1436 — the server writes notification times in the reader's zone;
  // for «по системе» it needs the device zone. Keyed on the id, not the object:
  // refreshUser hands over a new object for the same person.
  useEffect(() => {
    void syncDeviceTimezone(user?.id, (zone) => api.updateDeviceTimezone(zone));
  }, [user?.id]);

  const login = useCallback(async (data: LoginRequest, opts?: { returnTo?: string | null }) => {
    // CORE-1200 — код заводится ДО запроса: он едет заголовком на бэк, ложится
    // в first-party событие и возвращается вызывающему на ошибке, чтобы человек
    // мог назвать его поддержке.
    const attempt = beginAuthAttempt({ method: 'password', returnTo: opts?.returnTo });
    let response: Awaited<ReturnType<typeof api.login>>;
    try {
      response = await api.login(data, attempt.code);
    } catch (e) {
      // CORE-308 — surface auth failures (bad creds vs server/network) to
      // Sentry; the UI still shows its own friendly message via the rethrow.
      // CORE-1099 — «bad creds» больше не входит в это «surface»: за 90 дней
      // 108 из 137 событий потока auth на проде — INVALID_CREDENTIALS 401,
      // то есть человек опечатался в пароле и уже читает об этом в форме.
      // Считать их продолжает `analytics.track('login_error')` в LoginScreen
      // (с кодом ошибки) — там им и место. В Sentry остаётся то, что чинится
      // кодом: сеть без статуса, разбор ответа, 5xx, 429.
      // CORE-1200 — диагностическое событие уходит ВСЕГДА (по нему видно, дошёл
      // ли запрос вообще), а в Sentry — по тому же гварду, что и раньше.
      finishAuthFailure(attempt, e, { report: shouldReportApiError(e) });
      throw stampCorrelation(e, attempt.code);
    }
    finishAuthSuccess(attempt);
    await storage.setToken(response.token);
    const userData = await api.getMe();
    setUser(userData);

    // CORE-1137 — шов «анонимный визит → аккаунт». Строго после setToken:
    // `UserId` бэкенд берёт из JWT при отправке пачки, а без токена строка
    // ушла бы анонимной и связь бы не состоялась. Не ждём — вход не должен
    // упираться в аналитику.
    void trackIdentified({ method: 'password', isNewUser: false });

    // AMB-200 — start the proactive silent-refresh timer immediately so
    // any long-running session opened right after login is covered.
    scheduleRefresh(response.token);

    // Sync push subscription with backend
    syncPushSubscription();

    // Sync guest preferences to backend on login
    try {
      const guestPrefs = await storage.getGuestPreferences();
      if (guestPrefs) {
        await api.saveUserPreferences(guestPrefs);
        await storage.removeGuestPreferences();
      }
    } catch (error) {
      console.error('Failed to sync guest preferences:', error);
    }

    // Execute pending actions (e.g., subscribe to master)
    try {
      const pendingAction = await storage.getPendingAction();
      if (pendingAction) {
        if (pendingAction.type === 'subscribe_master') {
          await api.subscribeToMaster(pendingAction.masterId);
        }
        await storage.removePendingAction();
      }
    } catch (error) {
      console.error('Failed to execute pending action:', error);
      await storage.removePendingAction();
    }
  }, [scheduleRefresh]);

  // Mirror of login() but with the passkey ceremony in the middle. Same
  // post-login work (push, prefs, pending actions) so consumers don't
  // have to remember to call those after a passkey sign-in.
  const loginWithPasskey = useCallback(async (email?: string) => {
    if (Platform.OS !== 'web') {
      throw new Error('Passkey login is web-only for now.');
    }
    // Lazy-import the WebAuthn module so jest tests that don't exercise
    // this path don't have to deal with `navigator.credentials`.
    const { getAssertion } = await import('../services/webauthn');
    const attempt = beginAuthAttempt({ method: 'passkey' });
    let response: Awaited<ReturnType<typeof api.passkeyLoginComplete>>;
    try {
      const begin = await api.passkeyLoginBegin(email);
      const assertion = await getAssertion(begin.options);
      response = await api.passkeyLoginComplete({
        sessionId: begin.sessionId,
        assertionResponse: assertion,
      }, attempt.code);
    } catch (e) {
      // CORE-1099 — как и в login: отказ сервера (401/403/404) пользователь
      // уже видит, в Sentry идут только сбои. Отмена самой WebAuthn-церемонии
      // приходит DOMException без статуса и репортится намеренно: тем же
      // `NotAllowedError` браузер отвечает и на «нет платформенного
      // аутентификатора» / небезопасный контекст, а это уже наша проблема.
      finishAuthFailure(attempt, e, { report: shouldReportApiError(e) });
      throw stampCorrelation(e, attempt.code);
    }
    finishAuthSuccess(attempt);
    await storage.setToken(response.token);
    const userData = await api.getMe();
    setUser(userData);
    // CORE-1137 — см. login(): тот же шов, тот же порядок.
    void trackIdentified({ method: 'passkey', isNewUser: false });
    // AMB-200 — mirror the login() path: arm the silent-refresh timer.
    scheduleRefresh(response.token);
    syncPushSubscription();
    try {
      const guestPrefs = await storage.getGuestPreferences();
      if (guestPrefs) {
        await api.saveUserPreferences(guestPrefs);
        await storage.removeGuestPreferences();
      }
    } catch (error) {
      console.error('Failed to sync guest preferences:', error);
    }
    // Возвращаем, а не полагаемся на состояние: вызывающий решает прямо в
    // обработчике, а setState к этому моменту ещё не виден.
  }, [scheduleRefresh]);

  // CORE-108 — shared post-login work for social sign-in. Mirrors the tail of
  // login() / loginWithPasskey() so Google and Apple consumers get the same
  // session setup (token, user, silent-refresh timer, guest-pref sync).
  // CORE-513 — «этому человеку ещё не показали приветствие Искателя».
  const [newUserWelcomePending, setNewUserWelcomePending] = useState(false);
  const clearNewUserWelcome = useCallback(() => setNewUserWelcomePending(false), []);

  const completeSocialLogin = useCallback(async (
    response: { token: string; isNewUser?: boolean },
    method: 'google' | 'apple',
  ) => {
    // CORE-513 — признак первого входа держим в памяти, а не в хранилище.
    // Приветствие Искателя показывается один раз, сразу после входа: пережить
    // перезагрузку оно не должно, иначе человек увидит «Добро пожаловать» ещё
    // раз через неделю, открыв вкладку. Решает сервер (isNewUser), помнит —
    // текущая сессия вкладки.
    setNewUserWelcomePending(!!response.isNewUser);
    await storage.setToken(response.token);
    const userData = await api.getMe();
    setUser(userData);
    // CORE-1137 — здесь `isNewUser` знает сервер, поэтому регистрация через
    // соцвход отличима от возврата в воронке одним и тем же событием.
    void trackIdentified({ method, isNewUser: !!response.isNewUser });
    scheduleRefresh(response.token);
    syncPushSubscription();
    try {
      const guestPrefs = await storage.getGuestPreferences();
      if (guestPrefs) {
        await api.saveUserPreferences(guestPrefs);
        await storage.removeGuestPreferences();
      }
    } catch (error) {
      console.error('Failed to sync guest preferences:', error);
    }
    // Возвращаем, а не полагаемся на состояние: вызывающему нужно решение
    // прямо в обработчике, а setState к этому моменту ещё не виден.
    return { isNewUser: !!response.isNewUser };
  }, [scheduleRefresh]);

  // CORE-1534 — приложение открыло страницу оплаты в браузере с одноразовым
  // кодом: человек уже вошёл в приложении, и второй раз логиниться не должен.
  // Хвост тот же, что у входа паролем, кроме trackIdentified — это не новая
  // идентификация, а та же сессия в другом окне.
  const loginWithHandoffCode = useCallback(async (code: string) => {
    const response = await api.exchangeLoginHandoff(code);
    await storage.setToken(response.token);
    const userData = await api.getMe();
    setUser(userData);
    scheduleRefresh(response.token);
    syncPushSubscription();
  }, [scheduleRefresh]);

  const loginWithGoogleToken = useCallback(async (idToken: string) => {
    const attempt = beginAuthAttempt({ method: 'google' });
    let response: Awaited<ReturnType<typeof api.loginWithGoogle>>;
    try {
      response = await api.loginWithGoogle(idToken, attempt.code);
    } catch (e) {
      // CORE-1099 — см. login: ожидаемый отказ (401/403, «email уже занят» 400)
      // человек читает в форме, в Sentry остаются сеть и 5xx.
      finishAuthFailure(attempt, e, { report: shouldReportApiError(e) });
      throw stampCorrelation(e, attempt.code);
    }
    finishAuthSuccess(attempt);
    return completeSocialLogin(response, 'google');
  }, [completeSocialLogin]);

  // Platform-agnostic — both the web popup and the native Apple ceremony funnel
  // their identity token through here.
  const loginWithAppleToken = useCallback(async (payload: { idToken: string; fullName?: string }) => {
    const attempt = beginAuthAttempt({ method: 'apple' });
    let response: Awaited<ReturnType<typeof api.loginWithApple>>;
    try {
      response = await api.loginWithApple(payload, attempt.code);
    } catch (e) {
      // CORE-1099 — см. login: ожидаемый отказ (401/403, «email уже занят» 400)
      // человек читает в форме, в Sentry остаются сеть и 5xx.
      finishAuthFailure(attempt, e, { report: shouldReportApiError(e) });
      throw stampCorrelation(e, attempt.code);
    }
    finishAuthSuccess(attempt);
    return completeSocialLogin(response, 'apple');
  }, [completeSocialLogin]);

  const loginWithApple = useCallback(async () => {
    if (Platform.OS !== 'web') {
      // Native runs the Apple ceremony in SocialAuthButtons.native and calls
      // loginWithAppleToken directly.
      throw new Error('Use loginWithAppleToken on native.');
    }
    const { appleSignIn } = await safeImport(() => import('../utils/socialAuthWeb'));
    const { APPLE_WEB_SERVICES_ID } = await import('../config/socialAuth');
    const { idToken, fullName } = await appleSignIn(APPLE_WEB_SERVICES_ID);
    return loginWithAppleToken({ idToken, fullName });
  }, [loginWithAppleToken]);

  const register = useCallback(async (data: RegisterRequest, opts?: { returnTo?: string | null }) => {
    const attempt = beginAuthAttempt({ method: 'register', returnTo: opts?.returnTo });
    try {
      await api.register(data, attempt.code);
    } catch (e) {
      // CORE-1099 — см. login: ожидаемый отказ (401/403, «email уже занят» 400)
      // человек читает в форме, в Sentry остаются сеть и 5xx.
      finishAuthFailure(attempt, e, { report: shouldReportApiError(e) });
      throw stampCorrelation(e, attempt.code);
    }
    finishAuthSuccess(attempt);
    // Registration successful - user needs to confirm email before logging in
    //
    // CORE-1137 — узел «signup» в воронке привлечения. Строка уходит АНОНИМНОЙ,
    // и это не упущение: пользователя в этот момент ещё нет — есть только
    // сессия, подавшая заявку. `UserId` к той же сессии допишет вход после
    // подтверждения почты, и путь «кампания → signup → аккаунт» сходится по
    // `sessionId` без единой UTM в продуктовых событиях.
    void trackIdentified({ method: 'register', isNewUser: true });
  }, []);

  const logout = useCallback(async () => {
    // AMB-200 — kill the silent-refresh timer before clearing the token so
    // a stray tick can't grab the old token and then 401-loop into login.
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    // CORE-1791 — неотправленные черновики уходят вместе с аккаунтом (веб и
    // натив одинаково): на общем компьютере следующий человек не должен их
    // увидеть. Выход от этого не зависит — сбой хранилища его не останавливает.
    try {
      await clearChatDraftsOf(userIdRef.current);
    } catch {
      /* черновики — best-effort, как и их запись */
    }
    await storage.removeToken();
    setUser(null);
    // Иначе следующий, кто войдёт в этой же вкладке, унаследует чужое
    // «Добро пожаловать».
    setNewUserWelcomePending(false);
    setImpersonation(null);
    analytics.track('logout_success');
    
    if (Platform.OS === 'web') {
      try {
        const { navigationRef } = require('../navigation/rootNavigation');
        if (navigationRef.isReady()) {
          navigationRef.reset({
            index: 0,
            routes: [{ name: 'Home' }],
          });
        } else if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', '/');
        }
      } catch {
        if (typeof window !== 'undefined') window.history.replaceState(null, '', '/');
      }
    } else {
      try {
        const { navigateToLogin } = require('../navigation/rootNavigation');
        navigateToLogin();
      } catch (e) {
        console.warn('Navigation failed on logout', e);
      }
    }
  }, []);

  const expireSession = useCallback(async () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    await storage.removeToken();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const userData = await api.getMe();
    setUser(userData);
  }, []);

  const hasRole = useCallback((role: string): boolean => {
    return user?.roles?.includes(role) ?? false;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        loginWithPasskey,
        loginWithGoogleToken,
        loginWithApple,
        loginWithAppleToken,
        loginWithHandoffCode,
        register,
        logout,
        expireSession,
        refreshUser,
        hasRole,
        impersonation,
        newUserWelcomePending,
        clearNewUserWelcome,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
